import { useState, useEffect, useCallback } from "react";
import {
  Order,
  OrdersResponse,
  TodayOrdersCount,
  DateWiseOrdersResponse,
} from "../types/order.type";
import {
  fetchSellerOrders,
  fetchOrderById,
  updateOrderStatus,
  fetchTodayOrderCount,
  fetchDateWiseOrderCount,
} from "../API/axios";

export const useSellerOrders = () => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [totalOrders, setTotalOrders] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [todayCount, setTodayCount] = useState(0);
  const [dateWiseCount, setDateWiseCount] = useState<
    DateWiseOrdersResponse["orders"]
  >([]);

  const fetchOrders = useCallback(
    async (
      params: {
        page?: number;
        limit?: number;
        status?: string;
        date?: string;
      } = {},
    ) => {
      try {
        setLoading(true);
        const data = await fetchSellerOrders(params);
        setOrders(data.orders);
        setTotalOrders(data.total);
        setError(null);
      } catch (err: any) {
        setError(
          err.response?.data?.message ||
            err.message ||
            "Failed to fetch orders",
        );
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const getOrderDetails = async (orderId: string) => {
    try {
      setLoading(true);
      const data = await fetchOrderById(orderId);
      return data.order;
    } catch (err: any) {
      setError(
        err.response?.data?.message ||
          err.message ||
          "Failed to fetch order details",
      );
      return null;
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (orderId: string, status: string) => {
    try {
      setLoading(true);
      const data = await updateOrderStatus(orderId, status);
      setOrders((prev) =>
        prev.map((order) =>
          order.id === orderId
            ? { ...order, status: data.updatedOrder.status }
            : order,
        ),
      );
      return data.updatedOrder;
    } catch (err: any) {
      setError(
        err.response?.data?.message ||
          err.message ||
          "Failed to update order status",
      );
      return null;
    } finally {
      setLoading(false);
    }
  };

  const fetchDashboardStats = useCallback(async () => {
    try {
      const [todayData, dateWiseData] = await Promise.all([
        fetchTodayOrderCount(),
        fetchDateWiseOrderCount(),
      ]);
      setTodayCount(todayData.todayOrders);
      setDateWiseCount(dateWiseData.orders);
    } catch (err: any) {
      console.error("Failed to fetch dashboard stats", err);
    }
  }, []);

  useEffect(() => {
    fetchDashboardStats();
  }, [fetchDashboardStats]);

  return {
    orders,
    totalOrders,
    loading,
    error,
    todayCount,
    dateWiseCount,
    fetchOrders,
    getOrderDetails,
    updateStatus,
    fetchDashboardStats,
  };
};
